// theme.js
//
// MUI theme for bridge.bankcoin.capital. Follows the bankcoin-web "Hybrid
// premium" violet→indigo→cyan palette per the M6 design system. Owner
// requested "beautiful" — premium dark glass with subtle gradients.

import { createTheme, alpha } from '@mui/material/styles';

// Brand tokens — match react/bankcoin-web/src/styles/theme.js where present.
export const BRAND = {
  violet: '#7c3aed',          // violet-600 (primary)
  violetLight: '#a78bfa',     // violet-400
  violetDark: '#5b21b6',      // violet-800
  indigo: '#6366f1',          // indigo-500
  cyan: '#06b6d4',            // cyan-500
  cyanLight: '#22d3ee',       // cyan-400
  bg0: '#0a0a14',             // page background — near-black with violet bias
  bg1: '#13131f',             // card background
  bg2: '#1a1a28',             // elevated card
  border: '#2a2a3a',
  textPrimary: '#f5f5fb',
  textSecondary: '#9d9db0',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
};

// Linear gradient used in the hero + primary buttons.
export const GRADIENT = `linear-gradient(135deg, ${BRAND.violet} 0%, ${BRAND.indigo} 50%, ${BRAND.cyan} 100%)`;
export const GRADIENT_SOFT = `linear-gradient(135deg, ${alpha(BRAND.violet, 0.15)} 0%, ${alpha(BRAND.cyan, 0.08)} 100%)`;

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: BRAND.violet,
      light: BRAND.violetLight,
      dark: BRAND.violetDark,
      contrastText: '#ffffff',
    },
    secondary: { main: BRAND.cyan, light: BRAND.cyanLight },
    background: { default: BRAND.bg0, paper: BRAND.bg1 },
    text: { primary: BRAND.textPrimary, secondary: BRAND.textSecondary },
    success: { main: BRAND.success },
    warning: { main: BRAND.warning },
    error: { main: BRAND.error },
    divider: BRAND.border,
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.015em' },
    h4: { fontWeight: 700, letterSpacing: '-0.015em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage:
            'radial-gradient(at 20% 10%, rgba(124,58,237,0.18) 0px, transparent 50%),' +
            'radial-gradient(at 80% 0%, rgba(6,182,212,0.12) 0px, transparent 50%),' +
            'radial-gradient(at 50% 100%, rgba(99,102,241,0.10) 0px, transparent 60%)',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: alpha(BRAND.bg1, 0.7),
          backdropFilter: 'blur(12px)',
          border: `1px solid ${BRAND.border}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(BRAND.bg1, 0.72),
          backdropFilter: 'blur(14px)',
          border: `1px solid ${BRAND.border}`,
          borderRadius: 16,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: GRADIENT,
          boxShadow: `0 8px 24px ${alpha(BRAND.violet, 0.35)}`,
          '&:hover': {
            background: GRADIENT,
            filter: 'brightness(1.07)',
            boxShadow: `0 10px 30px ${alpha(BRAND.violet, 0.45)}`,
          },
          '&.Mui-disabled': {
            background: alpha(BRAND.violet, 0.35),
            color: alpha('#ffffff', 0.6),
          },
        },
        sizeLarge: { paddingTop: 14, paddingBottom: 14, fontSize: '1rem' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(BRAND.bg0, 0.6),
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${BRAND.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999 },
        bar: { borderRadius: 999 },
      },
    },
  },
});

export default theme;
